import { type CSSProperties, type ReactNode, useRef } from "react";

import {
  Overflow,
  OverflowItem,
  type OverflowItemProps,
  type OverflowProps,
} from "@fluentui/react-overflow";

import { cn } from "@btnet/utilities-core";

import {
  OverflowContext,
  useOverflowItemRegistration,
} from "./BuiOverflow.helpers";
import "./BuiOverflow.less";

/**
 * BuiOverflowProps defines the properties for the BuiOverflow component, which is a wrapper around the Overflow component from Fluent UI.
 */
export type BuiOverflowProps = Omit<OverflowProps, "children"> & {
  /**
   * The data-testid for the component.
   */
  testid: string;
  /**
   * Additional class name(s) to apply to the BuiOverflow component for styling purposes.
   */
  className?: string;
  /**
   * Additional inline styles to apply to the BuiOverflow component for styling purposes.
   */
  style?: CSSProperties;
  /**
   * The child elements to be rendered inside the BuiOverflow component. This should typically be a collection of BuiOverflowItem components.
   */
  children: ReactNode;
};

/**
 * BuiOverflowItemProps defines the properties for the BuiOverflowItem component, which represents an individual item within the BuiOverflow component.
 */
export type BuiOverflowItemProps = Omit<
  OverflowItemProps,
  "children" | "pinned" | "priority"
> & {
  /**
   * The content to be displayed when the item is in the overflow menu. If not provided, the content will be the same as the children.
   */
  overflowContent?: ReactNode;
  /**
   * The child element to be rendered inside the BuiOverflowItem component. This is the content that will be displayed in the main area of the BuiOverflow component.
   * If the item does not have overflowContent, the children will also be used as the content in the overflow menu.
   */
  children: ReactNode;
} & (
    | {
        /**
         * Indicates whether the item should be pinned to the main area of the BuiOverflow component. If true, the item will always be visible in the main area and will not be moved to the overflow menu, regardless of available space. If false or not provided, the item may be moved to the overflow menu based on available space and priority.
         */
        pinned?: boolean;
        priority?: never;
      }
    | {
        pinned?: never;
        /**
         * Indicates the priority of the item for determining which items should be moved to the overflow menu when there is not enough space in the main area. Items with lower priority values will be moved to the overflow menu before items with higher priority values. If not provided, the default priority is 0.
         */
        priority?: number;
      }
  );

/**
 * BuiOverflowMenuProps defines the properties for the BuiOverflowMenu component, which represents the menu that appears when items are moved to the overflow area. The ids property is an array of strings that represent the IDs of the items that should be included in the overflow menu.
 */
export interface BuiOverflowMenuProps {
  ids: string[];
}

/**
 * BuiOverflowMenuItemProps defines the properties for the BuiOverflowMenuItem component, which represents an individual item within the overflow menu. The id property is a string that represents the unique identifier of the item, which is used to determine which items should be included in the overflow menu based on the ids provided to the BuiOverflowMenu component.
 */
export type BuiOverflowMenuItemProps = Pick<OverflowItemProps, "id">;

/**
 * The BuiOverflowInternal component is a wrapper around the Overflow component from Fluent UI, providing additional functionality and styling specific to the Blueprint design system.
 * It allows for dynamic overflow of child elements based on available space, with support for pinned items and priority-based overflow.
 * @param props The props for the BuiOverflowInternal component.
 * @returns A JSX element representing the BuiOverflowInternal component.
 */
const BuiOverflowInternal = (props: BuiOverflowProps) => {
  const { testid, className, style, children, ...rest } = props;
  const registry = useRef<Map<string, ReactNode>>(new Map());
  return (
    <OverflowContext.Provider value={registry}>
      <Overflow {...rest}>
        <div
          data-testid={testid}
          className={cn("bui-overflow", className)}
          style={style}
        >
          {children}
        </div>
      </Overflow>
    </OverflowContext.Provider>
  );
};

/**
 * The BuiOverflowItem component represents an individual item within the BuiOverflow component.
 * It is a styled wrapper around the OverflowItem component from Fluent UI, with additional functionality for registering overflow content.
 * @param props The props for the BuiOverflowItem component. This includes overflowContent (the content to display in the overflow menu when the item is not visible in the main area), children (the content to display in the main area of the BuiOverflow component), and all other props from Fluent UI's OverflowItem component, such as id, pinned, and priority.
 * @returns A JSX element representing the BuiOverflowItem component.
 */
const BuiOverflowItem = ({
  overflowContent,
  children,
  id,
  ...rest
}: BuiOverflowItemProps) => {
  useOverflowItemRegistration(id, overflowContent ?? children);

  return (
    <OverflowItem id={id} {...rest}>
      <span>{children}</span>
    </OverflowItem>
  );
};

/**
 * BuiOverflow is the main component for handling overflow of child elements. It is an object that combines the BuiOverflowInternal component with a static Item property that references the BuiOverflowItem component.
 * This allows for a convenient API where users can use <BuiOverflow> as the main component and <BuiOverflow.Item> for individual items within the overflow.
 *
 * Props:
 * - testid: string (required) - The test ID for the BuiOverflow component, used for testing purposes.
 * - className: string (optional) - Additional class name(s) to apply to the BuiOverflow component for styling purposes.
 * - style: CSSProperties (optional) - Additional inline styles to apply to the BuiOverflow component for styling purposes.
 * - children: ReactNode (required) - The child elements to be rendered inside the BuiOverflow component. This should typically be a collection of BuiOverflowItem components.
 * - All other props from Fluent UI's Overflow component are also supported, such as padding for controlling the spacing between items.
 *
 * Example usage:
 * ```
 * <BuiOverflow testid="my-overflow">
 *   <BuiOverflow.Item id="item1" overflowContent="Item 1 in menu">Item 1</BuiOverflow.Item>
 *   <BuiOverflow.Item id="item2" overflowContent="Item 2 in menu">Item 2</BuiOverflow.Item>
 *   <BuiOverflow.Item id="item3" overflowContent="Item 3 in menu">Item 3</BuiOverflow.Item>
 * </BuiOverflow>
 * ```
 */
export const BuiOverflow = Object.assign(BuiOverflowInternal, {
  /**
   * BuiOverflowItem sub-component for individual overflow items.
   * A styled wrapper around Fluent UI OverflowItem with full access to its API.
   *
   * Props:
   * - id: string (required) - Unique identifier for the overflow item, used for registration and visibility tracking.
   * - overflowContent: ReactNode (optional) - Content to display in the overflow menu when the item is not visible in the main area. If not provided, the children will be used as the content in the overflow menu.
   * - children: ReactNode (required) - The content to display in the main area of the BuiOverflow component. This will also be used as the content in the overflow menu if overflowContent is not provided.
   * - All other props from Fluent UI's OverflowItem are also supported, such as pinned and priority for controlling overflow behavior.
   *
   * Example usage:
   * ```
   * <BuiOverflow.Item id="item1" overflowContent="Item 1 in menu">Item 1</BuiOverflow.Item>
   * ```
   */
  Item: BuiOverflowItem,
});
